import React, { useEffect, useState } from "react";
import { FaEdit, FaCheck, FaTimes, FaPlus, FaTrash } from "react-icons/fa";
import axios from "axios";
import API from "../config/config";
import Select from "react-select";

const optionSets = {
  tripType: ["One-Way", "Round-Trip", "Both"],
  carTransmission: ["Manual", "Manual and Automatic"],
  carType: ["SUV", "Sedan", "Hatchback", "Luxury"],
  city: [
    "Hyderabad",
    "Bangalore",
    "Delhi",
    "Mumbai",
    "Chennai",
    "Pune",
    "Ahmedabad",
    "Kolkata",
    "Surat",
    "Jaipur",
  ],
  // area removed, will use text input
};

const WEEK_DAYS = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

// const TIME_OPTIONS = (() => {
//   const options = [];

//   for (let hour = 0; hour < 24; hour += 1) {
//     const labelHour = hour % 12 === 0 ? 12 : hour % 12;
//     const suffix = hour < 12 ? "AM" : "PM";

//     options.push(`${labelHour}:00 ${suffix}`);
//     if (hour !== 24) {
//       options.push(`${labelHour}:30 ${suffix}`);
//     }
//   }

//   return options;
// })();

const TIME_OPTIONS = (() => {
  const options = [];

  for (let hour = 0; hour < 24; hour += 1) {
    const labelHour = hour % 12 === 0 ? 12 : hour % 12;
    const suffix = hour < 12 ? "AM" : "PM";

    options.push(`${labelHour}:00 ${suffix}`);
    options.push(`${labelHour}:30 ${suffix}`);
  }

  return options;
})();

const FULL_DAY_START_TIME = "12:00 AM";
const FULL_DAY_END_TIME = "11:59 PM";

const formatTimeTo12Hour = (timeStr) => {
  if (!timeStr) return "";

  const trimmed = String(timeStr).trim();
  const upper = trimmed.toUpperCase();

  if (upper === FULL_DAY_START_TIME) return FULL_DAY_START_TIME;
  if (upper === FULL_DAY_END_TIME) return FULL_DAY_END_TIME;

  const amPmMatch = trimmed.match(/^(\d{1,2})(?::(\d{2}))?\s*(AM|PM)$/i);
  if (amPmMatch) {
    const hour = parseInt(amPmMatch[1], 10);
    const minute = amPmMatch[2] || "00";
    const suffix = amPmMatch[3].toUpperCase();
    return `${hour}:${minute} ${suffix}`;
  }

  const hour24Match = trimmed.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
  if (hour24Match) {
    const hour = parseInt(hour24Match[1], 10);
    const minute = hour24Match[2];
    const suffix = hour < 12 ? "AM" : "PM";
    const hour12 = hour % 12 === 0 ? 12 : hour % 12;
    return `${hour12}:${minute} ${suffix}`;
  }

  return trimmed;
};

const DEFAULT_AVAILABLE_SLOT = {
  day: WEEK_DAYS[0],
  startTime: FULL_DAY_START_TIME,
  endTime: FULL_DAY_END_TIME,
};

const FULL_WEEK_AVAILABLE_TIMES = WEEK_DAYS.map((day) => ({
  day,
  startTime: FULL_DAY_START_TIME,
  endTime: FULL_DAY_END_TIME,
}));

// Specify which fields are multi-select
const multiSelectFields = ["carType"];

const DrivingChoices = ({ drivingChoices, editable, editableDetails, editableStatus, showIfEmpty = false, driverId, onUpdated }) => {
  const token = localStorage.getItem("adminToken");
  const detailsEditable = editableDetails !== undefined ? editableDetails : editable;
  const statusEditable = editableStatus !== undefined ? editableStatus : editable;

  const noChoices =
    !drivingChoices || Object.values(drivingChoices).every((val) => !val);

  const [editField, setEditField] = useState("");
  const [isEditingAvailableTimes, setIsEditingAvailableTimes] = useState(false);
  const [isOnline, setIsOnline] = useState(drivingChoices?.isOnline !== false);
  const [isSavingOnline, setIsSavingOnline] = useState(false);

  useEffect(() => {
    setIsOnline(drivingChoices?.isOnline !== false);
  }, [drivingChoices]);

  const isDriverOffline = !isOnline;

  const getInitialAvailableTimes = (availableTimes = drivingChoices?.availableTimes) => {
    if (!Array.isArray(availableTimes)) return [];

    return availableTimes
      .filter((slot) => slot && typeof slot === "object")
      .map((slot) => ({
        day: String(slot.day || "").trim(),
        startTime: formatTimeTo12Hour(slot.startTime),
        endTime: formatTimeTo12Hour(slot.endTime),
      }));
  };

  const getInitialFormData = (choices = drivingChoices) => ({
    tripType: choices?.tripType || "",
    carTransmission: choices?.carTransmission || "",
    carType: Array.isArray(choices?.carTypes)
      ? choices.carTypes
      : choices?.carTypes
      ? [choices.carTypes]
      : [],
    city: choices?.city || "",
    area: choices?.area || "",
    availableTimes: getInitialAvailableTimes(choices?.availableTimes),
  });

  const [formData, setFormData] = useState(() => getInitialFormData(drivingChoices));

  useEffect(() => {
    setFormData(getInitialFormData(drivingChoices));
  }, [drivingChoices]);

  // Show component if showIfEmpty is true, even when empty
  if (!showIfEmpty && noChoices) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSaveField = async (field) => {
    try {
      const payload = { [field]: formData[field] };
      
      // If drivingChoices record doesn't exist, create it
      if (!drivingChoices || !drivingChoices._id) {
        const createPayload = {
          ownerId: driverId,
          tripType: formData.tripType || "",
          carTransmission: formData.carTransmission || "",
          carTypes: formData.carType || [],
          city: formData.city || "",
          area: formData.area || "",
          ...payload,
        };

        await axios.post(API.ADMIN.SAVE_ADMIN_DRIVING_CHOICES, createPayload, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
      } else {
        await axios.patch(
          `${API.ADMIN.DRIVING_CHOICES}/${drivingChoices._id}`,
          payload,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
      }
      
      setEditField("");
      if (onUpdated) onUpdated();
    } catch (err) {
      console.error("Driving Choices update failed:", err);
      alert("Failed to update field");
    }
  };

  const isFullDaySlot = (slot) =>
    formatTimeTo12Hour(slot?.startTime) === FULL_DAY_START_TIME &&
    formatTimeTo12Hour(slot?.endTime) === FULL_DAY_END_TIME;

  const getDisplayedAvailableTimes = () => {
    if (Array.isArray(formData.availableTimes) && formData.availableTimes.length > 0) {
      return formData.availableTimes;
    }
    return FULL_WEEK_AVAILABLE_TIMES;
  };

  const formatAvailableTimeSlot = (slot) =>
    isFullDaySlot(slot)
      ? `${slot.day}: Full Day (12:00 AM – 11:59 PM)`
      : `${slot.day}: ${formatTimeTo12Hour(slot.startTime)} - ${formatTimeTo12Hour(slot.endTime)}`;

  const getEndTimeOptions = (startTime) => {
    if (startTime === FULL_DAY_START_TIME) {
      return [FULL_DAY_END_TIME];
    }

    const startIndex = TIME_OPTIONS.indexOf(startTime);

    if (startIndex === -1) {
      return [...TIME_OPTIONS, FULL_DAY_END_TIME];
    }

    const options = TIME_OPTIONS.slice(startIndex + 1);

    if (!options.includes(FULL_DAY_END_TIME)) {
      options.push(FULL_DAY_END_TIME);
    }

    return options;
  };

  const handleAvailableTimeChange = (index, key, value) => {
    setFormData((prev) => ({
      ...prev,
      availableTimes: prev.availableTimes.map((slot, i) => {
        if (i !== index) return slot;

        const updatedSlot = { ...slot, [key]: value };

        if (key === "startTime") {
          const endOptions = getEndTimeOptions(value);
          const currentEndTime = updatedSlot.endTime;

          if (!endOptions.includes(currentEndTime) || currentEndTime === value) {
            updatedSlot.endTime = endOptions[0] || value;
          }
        }

        return updatedSlot;
      }),
    }));
  };

  const handleAddAvailableTime = () => {
    setFormData((prev) => ({
      ...prev,
      availableTimes: [...prev.availableTimes, { ...DEFAULT_AVAILABLE_SLOT }],
    }));
  };

  const handleFullDayToggle = (index) => {
    setFormData((prev) => ({
      ...prev,
      availableTimes: prev.availableTimes.map((slot, i) =>
        i === index
          ? {
              ...slot,
              startTime: FULL_DAY_START_TIME,
              endTime: FULL_DAY_END_TIME,
            }
          : slot
      ),
    }));
  };

  const handleRemoveAvailableTime = (index) => {
    setFormData((prev) => ({
      ...prev,
      availableTimes: prev.availableTimes.filter((_, i) => i !== index),
    }));
  };

  const getNormalizedAvailableTimes = () =>
    (formData.availableTimes || [])
      .map((slot) => ({
        day: String(slot?.day || "").trim(),
        startTime: formatTimeTo12Hour(slot?.startTime),
        endTime: formatTimeTo12Hour(slot?.endTime),
      }))
      .filter(
        (slot) =>
          slot.day &&
          slot.startTime &&
          slot.endTime &&
          WEEK_DAYS.includes(slot.day)
      );

  const handleSaveAvailableTimes = async () => {
    try {
      const payload = { availableTimes: getNormalizedAvailableTimes() };
      if (!drivingChoices || !drivingChoices._id) {
        const createPayload = {
          ownerId: driverId,
          tripType: formData.tripType || "",
          carTransmission: formData.carTransmission || "",
          carTypes: formData.carType || [],
          city: formData.city || "",
          area: formData.area || "",
          ...payload,
        };
        await axios.post(API.ADMIN.SAVE_ADMIN_DRIVING_CHOICES, createPayload, {
          headers: { Authorization: `Bearer ${token}` },
        });
      } else {
        await axios.patch(`${API.ADMIN.DRIVING_CHOICES}/${drivingChoices._id}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }

      setIsEditingAvailableTimes(false);
      if (onUpdated) onUpdated();
    } catch (err) {
      console.error("Available times update failed:", err);
      alert("Failed to update available times");
    }
  };

  const handleToggleOnline = async () => {
    const nextValue = !isOnline;
    setIsOnline(nextValue);
    setIsSavingOnline(true);
    try {
      if (!drivingChoices || !drivingChoices._id) {
        const createPayload = {
          ownerId: driverId,
          tripType: formData.tripType || "",
          carTransmission: formData.carTransmission || "",
          carTypes: formData.carType || [],
          city: formData.city || "",
          area: formData.area || "",
          isOnline: nextValue,
        };
        await axios.post(API.ADMIN.SAVE_ADMIN_DRIVING_CHOICES, createPayload, {
          headers: { Authorization: `Bearer ${token}` },
        });
      } else {
        await axios.patch(
          `${API.ADMIN.DRIVING_CHOICES}/${drivingChoices._id}`,
          { isOnline: nextValue },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      if (onUpdated) onUpdated();
    } catch (err) {
      console.error("Online status update failed:", err);
      setIsOnline(!nextValue);
      alert("Failed to update online status");
    } finally {
      setIsSavingOnline(false);
    }
  };

  const handleCancelAvailableTimes = () => {
    setFormData((prev) => ({
      ...prev,
      availableTimes: getInitialAvailableTimes(drivingChoices?.availableTimes),
    }));
    setIsEditingAvailableTimes(false);
  };

  const renderEditableField = (label, fieldName) => {
    if (fieldName === "area") {
      return (
        <div key={fieldName}>
          <strong>{label}:</strong>{" "}
          {editField === fieldName ? (
            <div className="flex flex-col gap-2 mt-1">
              <input
                type="text"
                name="area"
                value={formData.area}
                onChange={handleChange}
                className="border px-2 py-1 rounded text-sm w-full h-auto"
                placeholder="Enter area name"
              />
              <div className="flex gap-3 items-center">
                <FaCheck
                  className="text-green-600 cursor-pointer"
                  onClick={() => handleSaveField(fieldName)}
                />
                <FaTimes
                  className="text-red-600 cursor-pointer"
                  onClick={() => {
                    setFormData((p) => ({
                      ...p,
                      [fieldName]: drivingChoices[fieldName] || "",
                    }));
                    setEditField("");
                  }}
                />
              </div>
            </div>
          ) : (
            <span className="flex items-center gap-2 mt-1 flex-wrap">
              {formData.area || (showIfEmpty && detailsEditable ? "Click to add" : "Not Set")}
              {detailsEditable && (
                <FaEdit
                  className="text-blue-600 cursor-pointer"
                  onClick={() => setEditField(fieldName)}
                />
              )}
            </span>
          )}
        </div>
      );
    }
    // ...existing code for other fields...
    const isMulti = multiSelectFields.includes(fieldName);
    const fieldValue = formData[fieldName];
    const options = optionSets[fieldName]?.map((opt) => ({
      label: opt,
      value: opt,
    }));
    const handleSelectChange = (selectedOptions) => {
      const values = selectedOptions.map((opt) => opt.value);
      setFormData((prev) => ({ ...prev, [fieldName]: values }));
    };
    return (
      <div key={fieldName}>
        <strong>{label}:</strong>{" "}
        {editField === fieldName ? (
          <div className="flex flex-col gap-2 mt-1">
            {isMulti ? (
              <Select
                isMulti
                options={options}
                value={options.filter((opt) => fieldValue.includes(opt.value))}
                onChange={handleSelectChange}
                className="text-sm"
                classNamePrefix="react-select"
              />
            ) : (
              <select
                name={fieldName}
                value={fieldValue}
                onChange={handleChange}
                className="border px-2 py-1 rounded text-sm w-full h-auto"
              >
                {optionSets[fieldName]?.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            )}
            <div className="flex gap-3 items-center">
              <FaCheck
                className="text-green-600 cursor-pointer"
                onClick={() => handleSaveField(fieldName)}
              />
              <FaTimes
                className="text-red-600 cursor-pointer"
                onClick={() => {
                  const original = drivingChoices[fieldName];
                  setFormData((p) => ({
                    ...p,
                    [fieldName]: isMulti
                      ? Array.isArray(original)
                        ? original
                        : original
                        ? [original]
                        : []
                      : original || "",
                  }));
                  setEditField("");
                }}
              />
            </div>
          </div>
        ) : (
          <span className="flex items-center gap-2 mt-1 flex-wrap">
            {isMulti
              ? Array.isArray(fieldValue) && fieldValue.length > 0
                ? fieldValue.join(", ")
                : (showIfEmpty && detailsEditable ? "Click to add" : "Not Set")
              : fieldValue || (showIfEmpty && detailsEditable ? "Click to add" : "Not Set")}
            {detailsEditable && (
              <FaEdit
                className="text-blue-600 cursor-pointer"
                onClick={() => setEditField(fieldName)}
              />
            )}
          </span>
        )}
      </div>
    );
  };

  return (
    <section className="p-6">
      <h2 className="text-2xl font-semibold text-gray-800 mb-6">
        Driving Choices
      </h2>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {renderEditableField("Trip Type", "tripType")}
        {renderEditableField("Car Transmission", "carTransmission")}
        {renderEditableField("Car Type", "carType")}
        {renderEditableField("City", "city")}
        {renderEditableField("Area", "area")}
      </div>

      <div className="mt-8 border border-gray-200 rounded-xl p-4 bg-gray-50">
        <div className="flex items-center justify-between gap-3 mb-3">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              Available Times (Optional) 
            </h3>
            <p className="text-sm text-gray-600">
              Choose the days and time range you are available. You can also use Full Day for 24-hour availability.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <span
                className={`text-sm font-medium ${
                  isOnline ? "text-green-600" : "text-gray-500"
                }`}
              >
                {isOnline ? "Online" : "Offline"}
              </span>
              <button
                type="button"
                role="switch"
                aria-checked={isOnline}
                disabled={!detailsEditable || isSavingOnline}
                onClick={handleToggleOnline}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                  isOnline ? "bg-green-500" : "bg-gray-300"
                }`}
              >
                <span
                  className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                    isOnline ? "translate-x-5" : "translate-x-1"
                  }`}
                />
              </button>
            </div>
            {detailsEditable && !isEditingAvailableTimes && (
              <button
                type="button"
                className="inline-flex items-center gap-2 rounded-lg bg-black text-white px-4 py-2 text-sm"
                onClick={() => setIsEditingAvailableTimes(true)}
              >
                <FaEdit className="text-xs" />
                Edit Times
              </button>
            )}
          </div>
        </div>

        {isEditingAvailableTimes ? (
          <>
            <div className="space-y-3">
              {(formData.availableTimes || []).map((slot, index) => (
                <div
                  key={`${slot.day}-${slot.startTime}-${slot.endTime}-${index}`}
                  className="grid grid-cols-1 md:grid-cols-[1fr_1fr_1fr_auto_auto] gap-3 items-end border border-gray-200 rounded-xl bg-white p-3"
                >
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Day
                    </label>
                    <select
                      className="w-full border rounded-lg px-3 py-2"
                      value={slot.day}
                      onChange={(e) =>
                        handleAvailableTimeChange(index, "day", e.target.value)
                      }
                    >
                      {WEEK_DAYS.map((day) => (
                        <option key={day} value={day}>
                          {day}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Start Time
                    </label>
                    <p className="text-xs text-gray-500 mb-1">Pick when your day begins</p>
                    <select
                      className="w-full border rounded-lg px-3 py-2"
                      value={slot.startTime}
                      onChange={(e) =>
                        handleAvailableTimeChange(index, "startTime", e.target.value)
                      }
                    >
                      {TIME_OPTIONS.map((time) => (
                        <option key={time} value={time}>
                          {time}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      End Time
                    </label>
                    <p className="text-xs text-gray-500 mb-1">The list starts after your chosen start time</p>
                    <select
                      className="w-full border rounded-lg px-3 py-2"
                      value={slot.endTime}
                      onChange={(e) =>
                        handleAvailableTimeChange(index, "endTime", e.target.value)
                      }
                    >
                      {getEndTimeOptions(slot.startTime).map((time) => (
                        <option key={time} value={time}>
                          {time}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Full Day
                    </label>
                    <p className="text-xs text-gray-500 mb-1">Use this for 24 hours</p>
                    <button
                      type="button"
                      onClick={() => handleFullDayToggle(index)}
                      className={`rounded-lg border px-3 py-2 text-sm font-medium ${
                        isFullDaySlot(slot)
                          ? "border-black bg-black text-white"
                          : "border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
                      }`}
                    >
                      {isFullDaySlot(slot) ? "Full Day" : "Set Full Day"}
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleRemoveAvailableTime(index)}
                    className="inline-flex items-center justify-center gap-2 bg-red-500 hover:bg-red-600 text-white rounded-lg px-4 py-2 text-sm"
                  >
                    <FaTrash className="text-xs" />
                    Remove
                  </button>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap items-center gap-2 mt-4">
              <button
                type="button"
                onClick={handleAddAvailableTime}
                className="inline-flex items-center gap-2 rounded-lg bg-black text-white px-4 py-2 text-sm"
              >
                <FaPlus className="text-xs" />
                Add Time
              </button>
              <button
                type="button"
                onClick={handleSaveAvailableTimes}
                className="inline-flex items-center gap-2 rounded-lg bg-green-600 hover:bg-green-700 text-white px-4 py-2 text-sm"
              >
                <FaCheck />
                Save Times
              </button>
              <button
                type="button"
                onClick={handleCancelAvailableTimes}
                className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white text-gray-700 px-4 py-2 text-sm"
              >
                <FaTimes />
                Cancel
              </button>
            </div>
          </>
        ) : (
          <div className="bg-white border border-gray-200 rounded-xl p-4">
            {isDriverOffline ? (
              <p className="text-sm text-gray-500 text-center">Driver is offline</p>
            ) : (
              <div className="space-y-2">
                {getDisplayedAvailableTimes().map((slot, index) => (
                  <p
                    key={`${slot.day}-${slot.startTime}-${slot.endTime}-${index}`}
                    className="text-sm text-gray-700"
                  >
                    {formatAvailableTimeSlot(slot)}
                  </p>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
};

export default DrivingChoices;
